import { BreadCrumbProvider } from "@components/breadCrumb";
import FocusManager, { FocusDirection } from "@components/focusManager";
import { Pane, SplitLayout } from "@components/splitLayout";
import { SplitPageWizardRow } from "@components/splitWizard/SplitPageWizardRow";
import { NoData } from "@components/table/NoData";
import Tabs, { ITabsProps } from "@components/tabs";
import { StyledScrollBar, TasksWrapper } from "@pages/fiscalYearClose/FiscalYearClose.styles";
import { isObjectEmpty } from "@utils/general";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { PaneStatus } from "../../enums";
import TestIds from "../../testIds";
import FileView from "../../views/fileView/FileView";
import { FormStorage } from "../../views/formView/FormStorage";
import { SmartHeaderStyled } from "../../views/formView/FormView.styles";
import View from "../../views/View";

interface IProps {
    storage: FormStorage;
    fileViewRef: React.RefObject<any>;
    hasFiles: boolean;
    selectedTaskId?: string;
    listTitle: string;
    tasks: ISplitPageWizardTask[];
    onTaskSelect?: (taskId: string) => void;
    tabsDefinition?: ITabsProps;
    renderForm: () => React.ReactNode;
}

export interface ISplitPageWizardTask {
    isChecked: boolean;
    isSelected: boolean;
    isDisabled: boolean;
    id: string;
    title: string;
}

export const SplitPageWizard: React.FC<IProps> = ({
                                                      storage,
                                                      fileViewRef,
                                                      hasFiles,
                                                      selectedTaskId,
                                                      listTitle,
                                                      tasks,
                                                      onTaskSelect,
                                                      tabsDefinition,
                                                      renderForm
                                                  }) => {

    const [paneStatus, setPaneStatus] = useState([PaneStatus.Normal, PaneStatus.Normal, PaneStatus.Collapsed]);
    const { t } = useTranslation("Components");

    const handlePaneStatusChange = useCallback((ps: PaneStatus[]) => {
        setPaneStatus(ps);
    }, [setPaneStatus]);

    const handleTaskSelect = useCallback((taskId: string) => {
        onTaskSelect(taskId);
    }, [onTaskSelect]);

    const isFormBusy = storage.isBusy() || storage.loading;

    function renderList() {
        return (<>
            <BreadCrumbProvider
                    back={storage?.initialHistoryState?.back}
                    removeLast={true}/>
            <View hotspotContextId={`${storage.data.definition.id}-list`}>
                <SmartHeaderStyled title={listTitle}
                                   shouldHideVariant={true}
                />
                <Tabs
                        additionalTabPrefix={t("Components:SplitWizard.TabPrefix")}
                        data={[]}
                        {...tabsDefinition}
                />
                <StyledScrollBar>
                    <FocusManager direction={FocusDirection.Vertical} disableLoop>
                        {({ itemProps, wrapperProps }) => (
                                <TasksWrapper data-testid={TestIds.Tasks}
                                              {...wrapperProps}
                                              tabIndex={-1}
                                              ref={wrapperProps.ref as React.RefObject<HTMLDivElement>}>
                                    {tasks?.map((task: ISplitPageWizardTask) => {
                                        return <SplitPageWizardRow
                                                key={task.id}
                                                itemProps={itemProps}
                                                taskId={task.id}
                                                title={task.title}
                                                isChecked={task.isChecked}
                                                isSelected={task.isSelected}
                                                isDisabled={task.isDisabled}
                                                isClosed={false} // TODO: implement isClosed logic if needed?
                                                onTaskSelect={handleTaskSelect}
                                        />;
                                    })}
                                </TasksWrapper>)}
                    </FocusManager>
                </StyledScrollBar>
            </View>
        </>);
    }

    if (isObjectEmpty(storage?.data.entity)) {
        return (<View hotspotContextId={`${storage.data.definition.id}-list`}>
            <SmartHeaderStyled title={listTitle}
                               shouldHideVariant={true}
            />
            <NoData/>
        </View>);
    }

    return (
            <SplitLayout onPaneStatusChanged={handlePaneStatusChange}
                         paneStatus={paneStatus}>
                <Pane visible={true} icon="List" width="50%">
                    {renderList()}
                </Pane>
                <Pane visible={!!selectedTaskId} icon="Form" width="50%" isBusy={isFormBusy}>
                    {renderForm()}
                </Pane>
                <Pane visible={!!selectedTaskId} width="50%"
                      icon={hasFiles ? "DocumentFilled" : "Document"}
                      isLast={true}
                      showPaneGradient={hasFiles}
                      showSplitterGradient={hasFiles}>
                    <FileView
                            isVisible={true}
                            ref={fileViewRef}
                            formStorage={storage}/>
                </Pane>
            </SplitLayout>
    );
};
